import { ClerkProvider } from '@clerk/react';
import { ui as clerkUi } from '@clerk/ui';
import { Outlet, useNavigate } from 'react-router-dom';

import { DevBypassBanner } from '@/components/auth/DevBypassBanner';
import { PostHogProvider } from '@/components/providers/PostHogProvider';
import { PostHogIdentityBridge } from '@/lib/analytics/posthog-identity';
import { AppLaunchSplashSafety } from '@/components/pwa/AppLaunchSplashSafety';
import { PwaRuntime } from '@/components/pwa/PwaRuntime';
import { Toaster } from '@/components/ui/toaster';
import { clerkAppearance, clerkLocalization } from '@/lib/auth/clerk-provider-appearance';
import { isDevAuthBypassEnabled } from '@/lib/dev/auth-bypass-flag';

function hasConfiguredEnv(value: string | undefined): value is string {
  const trimmed = value?.trim();
  return Boolean(trimmed && !trimmed.includes('...') && !trimmed.includes('<'));
}

/**
 * The app root: everything the Next root layout wrapped around every page.
 *
 * `routerPush`/`routerReplace` hand Clerk the React Router navigator. Without
 * them Clerk falls back to `window.location`, and a full document reload on
 * every auth redirect would tear down the Convex WebSocket and re-download the
 * bundle.
 */
export function RootLayout() {
  const navigate = useNavigate();
  const clerkPublishableKey = process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY;

  const app = (
    <>
      <PostHogProvider>
        <AppLaunchSplashSafety />
        <Outlet />
      </PostHogProvider>
      <Toaster />
      <PwaRuntime />
      <DevBypassBanner />
    </>
  );

  if (isDevAuthBypassEnabled || !hasConfiguredEnv(clerkPublishableKey)) return app;

  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      routerPush={(to: string) => navigate(to)}
      routerReplace={(to: string) => navigate(to, { replace: true })}
      signInUrl="/login"
      signUpUrl="/login"
      signInForceRedirectUrl="/trips/active"
      signUpForceRedirectUrl="/onboarding"
      afterSignOutUrl="/explore"
      appearance={clerkAppearance}
      localization={clerkLocalization}
      ui={clerkUi}
    >
      <PostHogIdentityBridge />
      {app}
    </ClerkProvider>
  );
}
